/**
 * oneEuro — a bank of 1€ filters (Casiez, Roussel, Vogel, CHI 2012), one per
 * channel, for the webcam face tracker (ENG-1107).
 *
 * A plain EMA trades jitter for lag everywhere; the 1€ filter raises its
 * cutoff with the signal's SPEED, so a blink (fast) is followed almost
 * unfiltered while a held pose (slow) is smoothed hard. `minCutoff` (Hz) is
 * the smoothing knob — lower = smoother at rest; `beta` scales the speed
 * term; `dCutoff` filters the derivative estimate itself.
 *
 * Pure and allocation-free after construction: `filter` reads `n` values,
 * writes `n` values, and keeps two Float64 state arrays. Works at any
 * timestamp cadence (the tracker's 15-30 Hz), never on the render loop.
 */
const TWO_PI = 2 * Math.PI

function alpha(cutoffHz, dtS) {
  const tau = 1 / (TWO_PI * cutoffHz)
  return 1 / (1 + tau / dtS)
}

/**
 * @param {number} n  channel count
 * @param {{minCutoff?: number, beta?: number, dCutoff?: number}} [cfg]
 */
export function createOneEuroBank(n, { minCutoff = 1.0, beta = 0.05, dCutoff = 1.0 } = {}) {
  const x = new Float64Array(n) // last filtered value
  const dx = new Float64Array(n) // last filtered derivative
  let lastT = NaN
  let cutoff = minCutoff

  return {
    setMinCutoff(hz) {
      cutoff = Math.max(1e-3, hz)
    },
    reset() {
      lastT = NaN
    },
    /**
     * @param {ArrayLike<number>} input  n values
     * @param {Float32Array|Float64Array} out  n values, written; may alias `input`
     * @param {number} tMs  sample time in ms
     */
    filter(input, out, tMs) {
      if (Number.isNaN(lastT) || tMs <= lastT) {
        for (let i = 0; i < n; i++) { x[i] = input[i]; dx[i] = 0; out[i] = input[i] }
        lastT = tMs
        return out
      }
      const dt = (tMs - lastT) / 1000
      lastT = tMs
      const aD = alpha(dCutoff, dt)
      for (let i = 0; i < n; i++) {
        const v = input[i]
        const rate = (v - x[i]) / dt
        const dHat = dx[i] + aD * (rate - dx[i])
        dx[i] = dHat
        const fc = cutoff + beta * Math.abs(dHat)
        const a = alpha(fc, dt)
        x[i] = x[i] + a * (v - x[i])
        out[i] = x[i]
      }
      return out
    },
  }
}